import { Link } from 'react-router-dom';

interface CTASectionProps {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  bgColor?: 'primary' | 'secondary' | 'dark';
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  subtitle,
  buttonText,
  buttonLink,
  bgColor = 'primary'
}) => {
  // Define background classes based on the bgColor prop
  const getBgClass = () => {
    switch (bgColor) {
      case 'secondary':
        return 'bg-gray-50';
      case 'dark':
        return 'bg-gray-900 text-white';
      case 'primary':
      default:
        return 'bg-gradient-to-r from-primary-dark to-primary text-white';
    }
  };

  // Define button classes based on the bgColor prop
  const getButtonClass = () => {
    switch (bgColor) {
      case 'secondary':
        return 'bg-primary text-white hover:bg-primary-dark shadow-soft hover:shadow-hover';
      case 'dark':
        return 'bg-accent text-white hover:bg-accent-dark shadow-soft hover:shadow-hover';
      case 'primary':
      default:
        return 'bg-white text-primary hover:bg-gray-100 shadow-soft hover:shadow-hover';
    }
  };

  // Define pattern bg for decoration
  const getPatternBackground = () => {
    switch (bgColor) {
      case 'secondary':
        return `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e5f9b' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;
      case 'dark':
      case 'primary':
      default:
        return `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;
    }
  };

  return (
    <section className={`py-20 ${getBgClass()} relative overflow-hidden`}>
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: getPatternBackground(), backgroundSize: '24px 24px' }}></div>
      
      <div className="container mx-auto px-4 text-center relative">
        <div className="max-w-4xl mx-auto">
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${bgColor === 'secondary' ? 'text-primary' : 'text-white'}`}>{title}</h2>
          <div className="w-24 h-1 bg-accent mx-auto mb-8"></div>
          <p className={`text-xl max-w-3xl mx-auto mb-10 ${bgColor === 'secondary' ? 'text-gray-600' : 'text-white/90'}`}>{subtitle}</p>
          <Link 
            to={buttonLink} 
            className={`btn ${getButtonClass()} text-lg px-10 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:-translate-y-1`}
          >
            {buttonText}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CTASection; 